/**
 * 메시지 박스 세로 위치 계산 — Phaser 무의존 순수 로직.
 *
 * `position`(top/middle/bottom)에 따른 초기 배치 + 컷아웃(강조 영역) 회피를 함께 처리한다.
 * top/bottom의 끝단 여백은 화면 높이의 {@link EDGE_INSET_RATIO}만큼 — 메시지가 화면 가장자리에
 * 바짝 붙지 않고 중앙 쪽으로 들어오게 한다(컷아웃 회피용 margin과는 분리).
 */
import type { TutorialMessagePosition, TutorialRect } from "./types.js";

/** 화면 높이 대비 top/bottom 끝단 여백 비율. */
export const EDGE_INSET_RATIO = 0.12;

/** 컷아웃 회피 시 사용하는 최소 여백(px). */
const CUTOUT_MARGIN = 16;

/** 위치 + 컷아웃 회피를 고려한 메시지 박스 중심 y. */
export function computeMessagePanelY(
  position: TutorialMessagePosition,
  panelH: number,
  cutout: TutorialRect | null,
  screenHeight: number,
): number {
  const H = screenHeight;
  const margin = CUTOUT_MARGIN;
  const edgeInset = Math.round(H * EDGE_INSET_RATIO);
  let y: number;
  if (position === "top") y = panelH / 2 + edgeInset;
  else if (position === "bottom") y = H - panelH / 2 - edgeInset;
  else y = H / 2;
  // 컷아웃과 겹치면 반대편으로 밀어 가독성 확보(clamp는 margin 유지 — 필요 시 끝단까지 회피 가능).
  if (cutout) {
    const top = y - panelH / 2;
    const bottom = y + panelH / 2;
    const overlaps = bottom > cutout.y - margin && top < cutout.y + cutout.height + margin;
    if (overlaps) {
      const spaceAbove = cutout.y;
      const spaceBelow = H - (cutout.y + cutout.height);
      y =
        spaceAbove >= spaceBelow
          ? Math.max(panelH / 2 + margin, cutout.y - panelH / 2 - margin)
          : Math.min(H - panelH / 2 - margin, cutout.y + cutout.height + panelH / 2 + margin);
    }
  }
  return y;
}
